import { useState, type FC } from 'react'
import type { TimelineElement } from '../../types/timelineElement'
import type { Workspace, WorkspaceRole } from '../../types/workspace'
import { computeBudgetBand } from '../../types/budget'
import { isBudgetEnabled, canMutateBudget } from '../../services/featureRegistry'
import { setBudgetLimit } from '../../services/budgetService'
import { useAuth } from '../../context/useAuth'
import { useDisplayName } from '../../hooks/useDisplayName'
import { useNls } from '../../nls/nlsProvider'
import { StatusChip, TextInput } from '../Common'
import './budgetLimitCard.css'

export interface NodeBudgetLimitCardProps {
  element: TimelineElement
  workspace: Workspace | null | undefined
  role: WorkspaceRole | null | undefined
  canEdit?: boolean
}

export const NodeBudgetLimitCard: FC<NodeBudgetLimitCardProps> = ({
  element,
  workspace,
  role,
  canEdit = true,
}) => {
  const { t } = useNls()
  const { currentUser, userProfile } = useAuth()
  const currentDisplayName = useDisplayName(userProfile ?? currentUser)

  const budgetModuleEnabled = isBudgetEnabled(workspace)
  const canMutate = canMutateBudget(workspace, role) && canEdit
  const currency = (workspace?.settings?.currency ?? 'USD').toUpperCase()

  const formatMinor = (minor: number) => `${currency} ${(minor / 100).toFixed(2)}`

  // Draft limit state in major currency units (e.g. "5000.00")
  const [draftValue, setDraftValue] = useState<string>(() =>
    element.budgetLimit !== null && element.budgetLimit !== undefined
      ? (element.budgetLimit / 100).toFixed(2)
      : ''
  )
  const [prevSyncKey, setPrevSyncKey] = useState<string>(
    `${element.id}:${element.budgetLimit ?? 'null'}`
  )
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [errorNotice, setErrorNotice] = useState<string | null>(null)

  // Sync draft value with element when element or its budgetLimit changes externally
  const currentSyncKey = `${element.id}:${element.budgetLimit ?? 'null'}`
  if (currentSyncKey !== prevSyncKey) {
    setPrevSyncKey(currentSyncKey)
    setDraftValue(
      element.budgetLimit !== null && element.budgetLimit !== undefined
        ? (element.budgetLimit / 100).toFixed(2)
        : ''
    )
    setErrorNotice(null)
  }

  const handleBlur = async () => {
    if (!canMutate || saving) return

    const trimmed = draftValue.trim()
    const nextMinor = trimmed.length === 0 ? null : Math.round(Number(trimmed.replace(/,/g, '.')) * 100)

    if (nextMinor !== null && (!Number.isFinite(nextMinor) || nextMinor < 0)) {
      setErrorNotice(t('NLSResourcesBudgetCardInvalidNumber'))
      return
    }

    const currentMinor = element.budgetLimit ?? null
    if (nextMinor === currentMinor) {
      setErrorNotice(null)
      return
    }

    setSaving(true)
    setErrorNotice(null)
    setSaved(false)

    try {
      await setBudgetLimit({
        workspaceId: element.workspaceId || workspace?.id || '',
        entity: {
          type: element.type === 'GOAL' ? 'GOAL' : 'TASK',
          id: element.id,
          docPath: `elements/${element.id}`,
        },
        budgetLimit: nextMinor,
        audit: {
          actorId: currentUser?.uid || '',
          actorName: currentDisplayName || currentUser?.email || null,
        },
        entityTitle: element.title,
      })
      setSaving(false)
      setSaved(true)
      setTimeout(() => {
        setSaved(false)
      }, 2500)
    } catch (error) {
      console.warn('[NodeBudgetLimitCard] setBudgetLimit failed', error)
      setSaving(false)
      setErrorNotice(t('NLSResourcesBudgetCardSaveFailed'))
    }
  }

  if (!budgetModuleEnabled) {
    return null
  }

  const actualSpend = typeof element.actualSpend === 'number' ? element.actualSpend : 0
  const budgetLimit = typeof element.budgetLimit === 'number' ? element.budgetLimit : null
  const band = computeBudgetBand(actualSpend, budgetLimit)
  const percentUsed = budgetLimit && budgetLimit > 0 ? (actualSpend / budgetLimit) * 100 : 0
  const clampedPercent = Math.min(100, Math.max(0, percentUsed))
  const isOverflow = percentUsed > 100

  const typeLabel = element.type === 'GOAL' ? 'Goal' : element.type === 'TASK' ? 'Task' : element.type

  return (
    <div className="nodeBudgetLimitCard" data-testid="node-budget-limit-card">
      <div className="nodeBudgetLimitCard__header">
        <div className="nodeBudgetLimitCard__eyebrowGroup">
          <span className="nodeBudgetLimitCard__eyebrow">{t('NLSResourcesBudgetCardEyebrow')}</span>
          <StatusChip size="sm" tone="neutral">{typeLabel}</StatusChip>
        </div>
        {saving && (
          <span className="nodeBudgetLimitCard__statusNotice isSaving">
            {t('NLSResourcesBudgetCardSaving')}
          </span>
        )}
        {!saving && saved && (
          <span className="nodeBudgetLimitCard__statusNotice isSaved">
            ✓ {t('NLSResourcesBudgetCardSaved')}
          </span>
        )}
      </div>

      <div className="nodeBudgetLimitCard__amountWrapper">
        {canMutate ? (
          <>
            <span className="nodeBudgetLimitCard__currency">{currency}</span>
            <TextInput
              type="number"
              inputMode="decimal"
              step="0.01"
              min={0}
              className="nodeBudgetLimitCard__input"
              value={draftValue}
              placeholder="0.00"
              disabled={saving}
              aria-label={t('NLSResourcesBudgetCardInputAria')}
              data-tour-id="node-budget-limit"
              onChange={(next) => {
                setDraftValue(next)
                if (errorNotice) setErrorNotice(null)
              }}
              onBlur={() => void handleBlur()}
            />
          </>
        ) : (
          <div className="nodeBudgetLimitCard__readonlyRow">
            <span className="nodeBudgetLimitCard__readonlyValue">
              {budgetLimit !== null ? formatMinor(budgetLimit) : `${currency} —`}
            </span>
            <StatusChip size="sm" tone="neutral">
              {t('NLSResourcesBudgetCardReadOnlyTag')}
            </StatusChip>
          </div>
        )}
      </div>

      {errorNotice && (
        <div className="nodeBudgetLimitCard__error">{errorNotice}</div>
      )}

      <div className="nodeBudgetLimitCard__meterSection">
        <div
          className={`nodeBudgetLimitCard__track ${budgetLimit === null ? 'isMuted' : ''} ${isOverflow ? 'isOverflow' : ''}`}
          data-testid="budget-meter-track"
        >
          <div
            className={`nodeBudgetLimitCard__fill isBand-${band}`}
            data-testid="budget-meter-fill"
            style={{ width: `${clampedPercent}%` }}
          />
        </div>
        <div className="nodeBudgetLimitCard__caption" data-testid="budget-meter-caption">
          {budgetLimit === null ? (
            <span>{t('NLSResourcesBudgetCardNoLimit')}</span>
          ) : (
            <span>
              {t('NLSResourcesBudgetCardMeterCaption', {
                spent: formatMinor(actualSpend),
                limit: formatMinor(budgetLimit),
                percent: Math.round(percentUsed),
              })}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
